// Copyright (c) 2026 Goffy. This file belongs to Goffy. All Rights Reserved.

import { useEffect } from 'react';

import { flushDebugLogs } from '@/services/appLogger';
import { getSharedBffWebSocket } from '@/services/bffWebSocket';

type Options = {
  apiBaseUrl: string;
  /** The round whose summary is open; null until it is known. */
  roundId: string | null;
  /** The signed-in user's token. Anonymous viewers keep their trail: their connection is read-only. */
  token?: string | null;
};

/**
 * Ships the debug trail the phone has been keeping once a round's summary opens. The round is
 * over on this device, the radio is awake for the summary anyway, and this is the one moment a
 * whole-round trail is worth a single burst. The hole screen's own socket is gone by now, so the
 * lines ride the shared connection. A second visit ships only what came after the first.
 *
 * Tagged `summaryRoundId` rather than `roundId`: every line already carries the round it was
 * written in, and a summary opened for an earlier round must not relabel them.
 */
export function useRoundTrailFlush({ apiBaseUrl, roundId, token }: Options): void {
  useEffect(() => {
    if (!roundId || !token) return;
    const socket = getSharedBffWebSocket(apiBaseUrl);
    socket.setAuthToken(token);
    flushDebugLogs(socket, 'round.summary', { summaryRoundId: roundId });
  }, [apiBaseUrl, roundId, token]);
}
